
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import VWrapper from '@/components/shared/VWrapper.vue';
import Notification from '@/mixins/Notification';
import api from '@/api/http/SmoothrPayApiService';
import { StatementDownloadStatus, StatementFormat, StatementStatus } from '@/enums/SmoothrPay';
import { MerchantConfiguration, StatementSummary, StatementTotals } from '@/interfaces/models/SmoothrPay';

/**
 * The settlement overviews (Abrechnungsübersichten) the onboarding service
 * generates per merchant and month, with a link to each rendering.
 *
 * Admin-only for now: the figures are platform-wide finance data.
 */
@Component({ components: { VWrapper } })
export default class SmoothrPayReports extends mixins(Notification) {
  public configurations: MerchantConfiguration[] = [];
  public statements: StatementSummary[] = [];
  public selectedId: string = '';
  /** `${statementId}:${format}` while its link is being fetched. */
  public downloading: string = '';

  get merchantItems() {
    return this.configurations.map((config: MerchantConfiguration) => ({
      text: config.legalEntity ? config.legalEntity.name : config._id,
      value: config._id,
    }));
  }

  public mounted() {
    this.fetchMerchants();
  }

  public async fetchMerchants() {
    this.$startLoading('smoothrPay.reports');
    try {
      const { data } = await api.getConfigurations({ page: 1, limit: 100 });
      this.configurations = data.data || [];
      // A single merchant needs no picking.
      if (this.configurations.length === 1) {
        this.selectedId = this.configurations[0]._id;
        await this.fetchStatements();
      }
    } catch (error) {
      this.notifyError('smoothrPay.notification.loadError');
    } finally {
      this.$stopLoading('smoothrPay.reports');
    }
  }

  public async fetchStatements() {
    if (!this.selectedId) {
      this.statements = [];
      return;
    }

    this.$startLoading('smoothrPay.reports');
    try {
      const { data } = await api.listStatements(this.selectedId);
      this.statements = data || [];
    } catch (error) {
      this.statements = [];
      this.notifyError('smoothrPay.reports.loadError');
    } finally {
      this.$stopLoading('smoothrPay.reports');
    }
  }

  /**
   * Asks for the signed link and opens it.
   *
   * The tab is opened on the click rather than after the request: a window.open
   * that is not on the click's own call stack is a popup, and mobile browsers
   * swallow it. A render that has not run yet answers `pending` — the request
   * queued it, so the tab closes again and the user is told to come back.
   */
  public async download(statementId: string, format: StatementFormat) {
    const tab: Window | null = window.open('', '_blank');
    this.downloading = `${statementId}:${format}`;
    try {
      const { data } = await api.getStatementDownload(this.selectedId, statementId, format);

      if (data.status === StatementDownloadStatus.READY && data.url) {
        if (tab && !tab.closed) {
          tab.location.href = data.url;
        }
        return;
      }

      this.closeTab(tab);
      this.notifySuccess('smoothrPay.reports.generating');
    } catch (error) {
      this.closeTab(tab);
      this.notifyError('smoothrPay.reports.downloadError');
    } finally {
      this.downloading = '';
    }
  }

  public closeTab(tab: Window | null) {
    if (tab && !tab.closed) {
      tab.close();
    }
  }

  public date(value?: string): string {
    return value ? new Date(value).toLocaleDateString(this.$i18n.locale) : '—';
  }

  public period(statement: StatementSummary): string {
    return statement.periodFrom ? `${this.date(statement.periodFrom)} – ${this.date(statement.periodTo)}` : '—';
  }

  /** Amounts are decimal strings — money, so they are never parsed to float to display. */
  public money(totals: StatementTotals | undefined, key: keyof StatementTotals): string {
    const amount = totals ? totals[key] : undefined;
    if (amount === undefined || amount === null || amount === '') {
      return '—';
    }
    return totals && totals.currency ? `${amount} ${totals.currency}` : `${amount}`;
  }

  public statusColor(status?: StatementStatus): string {
    if (status === StatementStatus.FAILED) {
      return 'error';
    }
    if (status === StatementStatus.PENDING) {
      return 'warning';
    }
    return 'success';
  }
}
